import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Send, 
  MessageSquare, 
  ChevronDown, 
  Clock, 
  CheckCircle2,
  HelpCircle,
  Lock,
  AlertCircle
} from 'lucide-react';
import emailjs from '@emailjs/browser';
import { auth } from "../firebase";

export default function Support() {
  const formRef = useRef();
  const [status, setStatus] = useState(""); // "", "sending", "success", "error"
  const [openFaq, setOpenFaq] = useState(null);
  const [userData, setUserData] = useState({ name: "", email: "" });

  useEffect(() => {
    const user = auth.currentUser;
    if (user) {
      setUserData({
        name: user.displayName || "",
        email: user.email || ""
      });
    }
    // Initialize with your Public Key
    emailjs.init("Zak5J_2Dm_ZGBz-VM"); 
  }, []);

  const sendEmail = async (e) => {
    e.preventDefault();
    setStatus("sending");

    try {
      // 1. Send the primary message to the support service
      const result = await emailjs.sendForm(
        'service_63oukws', 
        'template_ar6rv9q', 
        formRef.current
      );
      
      if (result.status === 200) {
        
        // 2. TRIGGER AUTO-REPLY TO SENDER
        // Uses the specific template_ar36rtd and maps variables correctly
        await emailjs.send(
          'service_63oukws',
          'template_ar36rtd',
          {
            name: formRef.current.user_name.value,
            email: formRef.current.user_email.value,
            subject: formRef.current.subject.value,
            message: formRef.current.message.value,
          }
        );

        setStatus("success");
        e.target.reset();
      } else {
        throw new Error("Failed to send");
      }
    } catch (error) {
      console.error("EmailJS Error:", error);
      setStatus("error");
      // Auto-reset error after 4 seconds
      setTimeout(() => setStatus(""), 4000);
    }
  };

  const faqs = [
    { q: "What is Focused Trader Journal?", a: "An all-in-one trading journal and account management platform designed to help traders track and analyze performance across multiple accounts—prop firm and personal—focusing on PnL, discipline, psychology, and consistency." },
    { q: "Who is it built for?", a: "Prop firm traders managing multiple phases, retail traders, Forex/Futures traders, and anyone aiming for long-term consistency through journaling and psychology." },
    { q: "Which markets are supported?", a: "Forex, Futures, Prop Firm accounts, and Personal trading accounts. The platform is market-agnostic." },
    { q: "Can I manage multiple accounts?", a: "Yes. Manage all accounts in one place with drawdown tracking for Prop Firms and broker-wise separation for personal accounts." },
    { q: "What trade data can I log?", a: "Entry/exit prices, risk, outcome, instrument, mindset/mental mistakes, screenshots, and detailed notes." },
    { q: "Does it track withdrawals?", a: "Yes. Log and track withdrawals across funded and personal accounts to understand real capital growth and equity changes." },
    { q: "What analytics are provided?", a: "Equity curves, win ratios, monthly PnL calendars, green/red day tracking, session performance, and mindset mistake analysis." },
    { q: "What makes it different?", a: "Built by a trader for traders. It eliminates clutter and combines performance data with deep psychological analysis." },
    { q: "Is this investment advice?", a: "No. It is an educational and analytical tool. All trading decisions and risks remain the sole responsibility of the user." },
    { q: "How does the Lot Calculator work?", a: "It calculates position size based on account balance, risk percentage, and instrument (Forex or Futures) to ensure consistent risk management." },
    { q: "Are news events included?", a: "Yes. A centralized economic calendar with impact level filters (Low/Medium/High) is built-in." },
    { q: "Is my data secure?", a: "Yes. We use secure cloud infrastructure. Your data is private and never shared with third parties." },
    { q: "Is it free?", a: "The first month is 100% free. From the second month onward, a subscription is required to access all features." },
    { q: "Can I cancel anytime?", a: "Yes. You can manage or cancel your subscription at any time before the next billing cycle." },
    { q: "How do I get support?", a: "Submit issues directly via this platform or reach out via the contact information provided here." },
    { q: "Is it suitable for beginners?", a: "Yes. It helps beginners build strong journaling habits and discipline from the very start." }
  ];

  return (
    <div style={styles.pageWrapper}>
      <style>
        {`
          .custom-scroll::-webkit-scrollbar { width: 6px; }
          .custom-scroll::-webkit-scrollbar-track { background: transparent; }
          .custom-scroll::-webkit-scrollbar-thumb { background: #1e293b; border-radius: 10px; }
          .custom-scroll::-webkit-scrollbar-thumb:hover { background: #334155; }
        `}
      </style>
      
      <div style={styles.glow} />
      <div style={styles.container}>
        
        <div style={styles.header}>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
            <span style={styles.badge}><Clock size={14} /> Average response: 2 hours</span>
            <h2 style={styles.title}>Knowledge Base & Support</h2>
            <p style={styles.subtitle}>Professional assistance for the Focused Trader.</p>
          </motion.div>
        </div>

        <div style={styles.contentGrid}>
          
          <motion.div style={styles.card} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
            <AnimatePresence mode="wait">
              {status === "success" ? (
                <motion.div key="success" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} style={styles.successState}>
                  <CheckCircle2 size={48} color="#38bdf8" />
                  <h3 style={{ marginTop: '20px', color: '#fff', fontWeight: '800' }}>Message Sent!</h3>
                  <p style={{ color: '#94a3b8' }}>We'll get back to you shortly.</p>
                  <button onClick={() => setStatus("")} style={styles.resetBtn}>Send another</button>
                </motion.div>
              ) : (
                <motion.form key="form" ref={formRef} onSubmit={sendEmail} style={styles.formStack}>
                  <div style={styles.cardHeader}>
                    <MessageSquare color="#38bdf8" size={20} />
                    <h3 style={styles.cardTitle}>Direct Message</h3>
                  </div>
                  
                  <div style={styles.inputRow}>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>Full Name</label>
                      <input name="user_name" type="text" defaultValue={userData.name} placeholder="Your name" style={styles.input} required />
                    </div>
                    <div style={styles.inputGroup}>
                      <label style={styles.label}>Email Address</label>
                      <input name="user_email" type="email" defaultValue={userData.email} placeholder="Email" style={styles.input} required />
                    </div>
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.label}>Subject</label>
                    <input name="subject" type="text" placeholder="What's this regarding?" style={styles.input} required />
                  </div>

                  <div style={{...styles.inputGroup, flex: 1, marginBottom: 0}}>
                    <label style={styles.label}>Message</label>
                    <textarea 
                      name="message" 
                      className="custom-scroll" 
                      placeholder="Provide as much detail as possible..." 
                      style={styles.textarea} 
                      required 
                    />
                  </div>

                  {status === "error" && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={styles.errorMsg}>
                      <AlertCircle size={14} /> Something went wrong. Please check your connection.
                    </motion.div>
                  )}

                  <button type="submit" disabled={status === "sending"} style={{...styles.submitBtn, opacity: status === "sending" ? 0.7 : 1}}>
                    {status === "sending" ? "Sending..." : "Send Message"} 
                    <Send size={18} style={{ marginLeft: '8px' }} />
                  </button>
                </motion.form>
              )}
            </AnimatePresence>
          </motion.div>

          <motion.div style={styles.card} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
            <div style={styles.cardHeader}>
              <HelpCircle color="#38bdf8" size={20} />
              <h3 style={styles.cardTitle}>Common Questions</h3>
            </div>
            
            <div className="custom-scroll" style={styles.faqScrollArea}>
              {faqs.map((faq, index) => (
                <div 
                  key={index} 
                  style={{
                    ...styles.faqItem,
                    borderColor: openFaq === index ? '#38bdf8' : '#1e293b',
                    background: openFaq === index ? 'rgba(56, 189, 248, 0.03)' : 'transparent'
                  }} 
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                >
                  <div style={styles.faqHeader}>
                    <span style={{...styles.faqQuestion, color: openFaq === index ? '#38bdf8' : '#f1f5f9'}}>{faq.q}</span>
                    <ChevronDown size={18} style={{ transform: openFaq === index ? 'rotate(180deg)' : 'rotate(0)', transition: '0.3s', color: openFaq === index ? '#38bdf8' : '#64748b'}} />
                  </div>
                  <AnimatePresence>
                    {openFaq === index && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} style={styles.faqAnswer}>
                        {faq.a}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
            
            <div style={styles.securityNote}>
              <Lock size={12} color="#10b981" />
              <span>AES-256 Encrypted Support Channel</span>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  pageWrapper: { background: "Transparent", minHeight: "100vh", width: "100%", position: 'relative', overflow: 'hidden' },
  glow: { position: 'absolute', top: '-10%', left: '50%', transform: 'translateX(-50%)', width: '70vw', height: '50vw', background: 'radial-gradient(circle, rgba(56, 189, 248, 0.05) 0%, transparent 70%)', filter: 'blur(100px)', pointerEvents: 'none' },
  container: { maxWidth: '1200px', margin: '0 auto', padding: '60px 20px', color: '#fff', fontFamily: "'Inter', sans-serif", position: 'relative', zIndex: 1 },
  header: { textAlign: 'center', marginBottom: '30px' },
  badge: { display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(56, 189, 248, 0.1)', color: '#38bdf8', padding: '6px 14px', borderRadius: '100px', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase' },
  title: { fontSize: '42px', fontWeight: '900', marginBottom: '8px', letterSpacing: '-1.5px', color: '#f8fafc' },
  subtitle: { color: '#64748b', fontSize: '15px' },
  contentGrid: { display: 'grid', gridTemplateColumns: '1.1fr 1fr', gap: '24px', alignItems: 'stretch' },
  card: { background: 'rgba(10, 15, 26, 0.7)', backdropFilter: 'blur(12px)', border: '1px solid #1e293b', padding: '28px', borderRadius: '24px', display: 'flex', flexDirection: 'column' },
  cardHeader: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' },
  cardTitle: { fontSize: '18px', fontWeight: '800', color: '#f8fafc' },
  formStack: { display: 'flex', flexDirection: 'column', height: '100%' },
  inputRow: { display: 'flex', gap: '16px', marginBottom: '12px' }, 
  inputGroup: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: '0', marginBottom: '12px' }, 
  label: { display: 'block', fontSize: '10px', fontWeight: '800', color: '#475569', marginBottom: '6px', textTransform: 'uppercase' },
  input: { width: '100%', background: '#020617', border: '1px solid #1e293b', padding: '12px', borderRadius: '10px', color: '#fff', outline: 'none', boxSizing: 'border-box' },
  textarea: { width: '100%', background: '#020617', border: '1px solid #1e293b', padding: '14px', borderRadius: '10px', color: '#fff', flex: 1, minHeight: '300px', outline: 'none', resize: 'none' },
  submitBtn: { width: '100%', background: '#38bdf8', color: '#000', padding: '16px', borderRadius: '10px', fontWeight: '900', border: 'none', cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '12px' },
  errorMsg: { color: '#ef4444', fontSize: '12px', marginTop: '10px', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px' },
  faqScrollArea: { flex: 1, overflowY: 'auto', paddingRight: '12px', maxHeight: '580px' },
  faqItem: { border: '1px solid #1e293b', borderRadius: '14px', padding: '16px', cursor: 'pointer', marginBottom: '10px' },
  faqHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '15px' },
  faqQuestion: { fontWeight: '700', fontSize: '14px' },
  faqAnswer: { marginTop: '14px', color: '#94a3b8', fontSize: '13px', lineHeight: '1.6' },
  securityNote: { marginTop: '24px', display: 'flex', alignItems: 'center', gap: '8px', color: '#334155', fontSize: '11px', justifyContent: 'center', fontWeight: '700' },
  successState: { textAlign: 'center', padding: '60px 0', display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'center' },
  resetBtn: { marginTop: '24px', background: 'transparent', border: '1px solid #1e293b', color: '#94a3b8', padding: '10px 20px', borderRadius: '10px', cursor: 'pointer' },
};